import { cn } from '../../utils';

type Stat = {
    id: string;
    name: string;
    value: React.ReactNode;
    trend?: Trend;
    metric?: string;
};

export enum StatDisplay {
    BORDER = 'border',
    CARD = 'card',
    CARD_GROUP = 'cardgroup',
}

export default function Stats({
    label,
    stats,
    colNumber = 2,
    display = StatDisplay.BORDER,
    responsive = false,
    ...rest
}: {
    label?: string;
    stats: Stat[];
    colNumber?: number;
    display?: StatDisplay;
    responsive?: boolean;
}) {
    const cols: Record<number, string> = {
        1: 'alli-grid-cols-1',
        2: 'alli-grid-cols-2',
        3: 'alli-grid-cols-3',
        4: 'alli-grid-cols-4',
    };
    const isCardGroup = display === StatDisplay.CARD_GROUP;

    return (
        <div {...rest}>
            {label && (
                <div className="alli-medium alli-mb-2 alli-text-base alli-text-blue-gray-700">
                    {label}
                </div>
            )}
            <div
                data-testid="stats-items-container"
                className={cn(
                    'alli-grid',
                    responsive
                        ? 'alli-auto-cols-max alli-grid-flow-col'
                        : cols[colNumber],
                    {
                        'alli-divide-x alli-divide-gray-400 alli-rounded-lg':
                            isCardGroup,

                        'alli-shadow':
                            colNumber === stats.length &&
                            isCardGroup &&
                            !responsive,
                        'alli-gap-x-2': responsive && !isCardGroup,
                        'alli-gap-5': !responsive && !isCardGroup,
                    },
                )}>
                {stats.map((stat, index) => (
                    <StatItem
                        isFirst={index === 0}
                        isLast={stats.length - 1 === index}
                        key={`stats-${stat.id}`}
                        stat={stat}
                        display={display}
                        size={responsive ? StatSize.COMPACT : StatSize.REGULAR}
                    />
                ))}
            </div>
        </div>
    );
}

enum StatSize {
    COMPACT = 'sm',
    REGULAR = 'lg',
}

function StatItem({
    stat,
    display,
    isFirst,
    isLast,
    size = StatSize.REGULAR,
}: {
    stat: Stat;
    display: StatDisplay;
    isFirst: boolean;
    isLast: boolean;
    size: StatSize;
}) {
    const isCardGroup = display === StatDisplay.CARD_GROUP;
    const isBorder = display === StatDisplay.BORDER;
    const isCard = display === StatDisplay.CARD;
    const isRegularSize = size === StatSize.REGULAR;
    const isCompactSize = size === StatSize.COMPACT;

    return (
        <div
            data-testid={`stats-item-${stat.id}`}
            className={cn('alli-bg-white', {
                'alli-border-l alli-border-gray-400 alli-pl-6':
                    isBorder && isRegularSize,
                'alli-border-l alli-border-gray-400 alli-pl-2':
                    isBorder && isCompactSize,
                'alli-rounded-lg alli-p-6 alli-shadow': isCard && isRegularSize,
                'alli-rounded-lg alli-p-2 alli-shadow': isCard && isCompactSize,
                'alli-p-6': isCardGroup && isRegularSize,
                'alli-p-2': isCardGroup && isCompactSize,
                'alli-rounded-l-lg': isFirst && isCardGroup,
                'alli-rounded-r-lg': isLast && isCardGroup,
            })}>
            <StatInternal stat={stat} isRegularSize={isRegularSize} />
        </div>
    );
}

function StatInternal({
    stat,
    isRegularSize = true,
}: {
    stat: Stat;
    isRegularSize?: boolean;
}) {
    const isCompactSize = !isRegularSize;

    return (
        <div
            className={cn(
                'alli-flex alli-flex-col',
                isRegularSize ? 'alli-gap-y-1' : 'alli-gap-y-0.5',
            )}>
            <div
                className={cn('alli-font-medium alli-text-gray-700', {
                    'alli-text-sm alli-leading-6': isRegularSize,
                    'alli-text-xs alli-leading-4': isCompactSize,
                })}>
                {stat.name}
            </div>
            <div className="alli-flex alli-items-center alli-justify-between">
                <div
                    className={cn('alli-flex alli-items-center', {
                        'alli-space-x-2': isRegularSize,
                        'alli-space-x-1': isCompactSize,
                    })}>
                    <div
                        className={cn(
                            'alli-font-normal alli-tracking-tight alli-text-blue-gray-700',
                            {
                                'alli-text-2xl': isRegularSize,
                                'alli-text-md': isCompactSize,
                            },
                        )}>
                        {stat.value}
                    </div>
                    {stat.metric && (
                        <div
                            className={cn(
                                'alli-font-normal alli-text-gray-700',
                                {
                                    'alli-text-sm alli-leading-6':
                                        isRegularSize,
                                    'alli-text-xs alli-leading-5':
                                        isCompactSize,
                                },
                            )}>
                            {stat.metric}
                        </div>
                    )}
                </div>
                {stat.trend && (
                    <StatVisual
                        trend={stat.trend}
                        isRegularSize={isRegularSize}
                    />
                )}
            </div>
        </div>
    );
}

export enum TrendDirection {
    UP = 'up',
    DOWN = 'down',
}

type Trend = {
    direction: TrendDirection;
    value: string;
};

function StatVisual({
    trend,
    isRegularSize = true,
}: {
    trend?: Trend;
    isRegularSize?: boolean;
}) {
    if (!trend) return null;
    return (
        <div
            className={cn(
                'alli-flex alli-items-center alli-space-x-0.5 alli-rounded-md alli-font-medium',
                isRegularSize
                    ? 'alli-px-2 alli-py-1 alli-text-sm'
                    : 'alli-px-1 alli-py-0.5 alli-text-xs',
                {
                    'alli-bg-green-100 alli-text-green-700':
                        trend.direction === TrendDirection.UP,
                    'alli-bg-red-100 alli-text-red-900':
                        trend.direction === TrendDirection.DOWN,
                },
            )}>
            <div>{trend.value}</div>
            <div
                className={cn({
                    'alli-rotate-180 alli-text-red-300':
                        trend.direction === TrendDirection.DOWN,
                })}>
                <Arrow />
            </div>
        </div>
    );
}

function Arrow() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.70503 5.395C3.43166 5.12163 3.43166 4.67842 3.70503 4.40505L6.50503 1.60505C6.77839 1.33168 7.22161 1.33168 7.49497 1.60505L10.295 4.40505C10.5683 4.67842 10.5683 5.12163 10.295 5.395C10.0216 5.66837 9.57839 5.66837 9.30503 5.395L7.7 3.78997L7.7 11.9C7.7 12.2866 7.3866 12.6 7 12.6C6.6134 12.6 6.3 12.2866 6.3 11.9L6.3 3.78997L4.69497 5.395C4.42161 5.66837 3.97839 5.66837 3.70503 5.395Z"
                className="alli-fill-current"
            />
        </svg>
    );
}
